/**
 * **Startup/reconnect contract step 8** — *"Reconcile receipts for BLE actions that **already
 * completed**; never replay an open."*
 *
 * P6 built every other step of `device-cache.md`'s contract and left this one with no live path
 * (its own table says `8 · reconcile completed-BLE receipts — **P16** (D94)`). This module is the
 * path, and it is deliberately **one exported function** so that each caller gains a call rather
 * than a feature: the queue, the upload, the retry policy, and the account partition all stay
 * here. **There are four callers**, three in P6's provider — boot, D128's reconnect, the app bar's
 * refresh — and one in `localOpen.ts`, which drains the receipt it has just written when the phone
 * has reception. That fourth one is why the first three are no longer the whole list: they all
 * presume a phone that *had been offline*, and D189(e) made BLE a live transport for one that
 * never was.
 *
 * **What the caller may assume, and what it may not.** A drain never opens a gate — it cannot,
 * because `drainOutbox` takes an uploader and the only uploader is
 * `POST /gates/{id}/receipts`, a route whose Lambda holds no broker credential at all
 * (`infra/ble.ts`). §13 asks for *"a network trace of the whole reconnect shows no gate command
 * of any kind"*, and that trace is empty on three independent grounds: the row has no command
 * field, the client calls one endpoint, and the server could not dial a broker if it tried.
 */

import { NetworkError } from '../auth/transport'
import type { KeyValueStore } from '../device-cache/store'
import type { ApiRequestFn } from '../screens/shared/api'
import { uploadReceipt } from './api'
import { drainOutbox, toUpload, type QueuedReceipt, type UploadOutcome } from './outbox'

export interface ReconcileResult {
  readonly acknowledged: number
  readonly remaining: number
}

/**
 * Upload every queued receipt for one account, deleting each as it is acknowledged.
 *
 * **Single-flighted**, because the triggers overlap by design: D128's reconnect sequence, a boot
 * that finds reception, the app bar's refresh, and a BLE open's own drain can all fire within the
 * same second, and concurrent drains would upload the same receipt several times. The server would
 * dedupe (PEC-12), so the cost is requests rather than rows — but they are requests against the
 * same per-caller ceiling (D68/D90) that the resident's next real action needs.
 *
 * 🔸 **What joining costs, stated because it is invisible from the call site.** `drainOutbox` takes
 * its `readOutbox` snapshot when it starts, so a caller that joins an in-flight drain gets that
 * drain's rows and its outcome — **not a drain of the queue as it stands now.** A receipt written
 * while one is running is therefore not in it. Nothing is lost or duplicated: the row is durable
 * and the next trigger of any kind takes it, oldest opening first. Accepted rather than handled,
 * because a trailing re-run would change what this promise means for all four callers to close a
 * window that self-heals on the next tap.
 */
let draining: Promise<ReconcileResult> | undefined

export function reconcileReceipts(
  store: KeyValueStore,
  stage: string,
  userId: string,
  request: ApiRequestFn,
): Promise<ReconcileResult> {
  draining ??= runDrain(store, stage, userId, request).finally(() => {
    draining = undefined
  })
  return draining
}

async function runDrain(
  store: KeyValueStore,
  stage: string,
  userId: string,
  request: ApiRequestFn,
): Promise<ReconcileResult> {
  return drainOutbox(store, stage, userId, async (receipt: QueuedReceipt): Promise<UploadOutcome> => {
    try {
      const response = await uploadReceipt(request, receipt.gateId, toUpload(receipt))
      if (response.ok) return 'acknowledged'

      /*
       * **A `4xx` other than `401`, `408`, or `429` is terminal for this receipt, and deleting it
       * is the correct answer rather than the convenient one.** The realistic cases are a gate
       * the resident has since been removed from (`403`) and a body this build cannot fix
       * (`422`); retrying either forever spends the caller's ceiling on a request that can never
       * succeed, and the row would outlive the app.
       *
       * **`401` is excluded deliberately.** It is the one status that means *try again with a
       * fresh token*, and `SessionManager.request()` has already retried once behind a refresh
       * before this line sees it — so a `401` here is a session ending, and the receipt must
       * survive the sign-in that follows. (If the session is ending because the subject was
       * erased, P6's purge takes the row with the database, which is D69's client half.)
       *
       * **`429` is excluded for the same reason `401` is: it is not a verdict on this receipt.**
       * The route is `metered` and shares its per-caller ceiling with every other call the drain
       * runs beside — Home's reads, the credential warm, the tap itself — so a `429` here says
       * *this caller*, not *this body*, and deleting the receipt on it is how a real BLE open (or
       * a real failed attempt) stops appearing in Activity for good, permanently and silently.
       * **`408` reads the same way**: a request that timed out was never answered at all, which is
       * the opposite of the "a body this build cannot fix" argument the terminal branch rests on.
       */
      if (response.status === 401 || response.status === 408 || response.status === 429) {
        return 'retry'
      }
      return response.status >= 400 && response.status < 500 ? 'acknowledged' : 'retry'
    } catch (error) {
      // Offline again, or the server could not serve the session. Both mean *later*, and only a
      // `401` ever means *never* — the same rule the rest of the client keeps (D129).
      if (error instanceof NetworkError) return 'retry'
      throw error
    }
  })
}

/** Test-only: forget the in-flight drain so a suite's next call starts clean. */
export function resetReconcileForTests(): void {
  draining = undefined
}
